import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import DocumentManagement from "@/components/DocumentManagement";
import ServiceManagement from "@/components/ServiceManagement";
import PaymentManagement from "@/components/PaymentManagement";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard, Palette, Settings, FileText, Users, Car, Route,
  LogOut, TrendingUp, DollarSign, Clock, UserCheck, Plus, Edit, Trash2,
  XCircle, UserPlus, Ban, CheckCircle, Shield, Eye, FileCheck, Upload,
  RefreshCw, Crown, Search, MoreVertical, ChevronDown, Globe, Mail, Phone,
  MapPin, Image, Type, Hash, Save, AlertTriangle, Star, Activity, BarChart3,
  Share2, Instagram, Facebook, MessageCircle, Twitter, CreditCard, Wallet,
  QrCode, IndianRupee, Percent, Zap
} from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const statusColors = {
  requested: "bg-amber-100 text-amber-700",
  assigned: "bg-blue-100 text-blue-700",
  accepted: "bg-indigo-100 text-indigo-700",
  started: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const reportStatusColors = {
  pending: "bg-amber-100 text-amber-700",
  investigating: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  dismissed: "bg-slate-100 text-slate-600",
};

const sidebarItems = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, desc: "Analytics & Stats" },
  { key: "analytics", label: "Analytics", icon: BarChart3, desc: "Charts & Reports" },
  { key: "services", label: "Services", icon: Zap, desc: "Service Management" },
  { key: "payments", label: "Payments", icon: CreditCard, desc: "Payment Dashboard" },
  { key: "branding", label: "Branding", icon: Palette, desc: "Logo & Colors" },
  { key: "platform", label: "Platform Settings", icon: Settings, desc: "Core Configuration" },
  { key: "fares", label: "Fares & Routes", icon: MapPin, desc: "Destinations & Pricing" },
  { key: "documents", label: "Documents", icon: FileCheck, desc: "Document Management" },
  { key: "cms", label: "CMS", icon: FileText, desc: "Content Management" },
  { key: "users", label: "User Management", icon: Users, desc: "All Users" },
  { key: "drivers", label: "Driver Management", icon: Car, desc: "Drivers & Documents" },
  { key: "rides", label: "Ride Control", icon: Route, desc: "All Rides" },
  { key: "reports", label: "Reports & Issues", icon: AlertTriangle, desc: "User Reports" },
];

export default function SuperAdminDashboard() {
  const { user, token, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data states
  const [analytics, setAnalytics] = useState({});
  const [branding, setBranding] = useState({});
  const [platformSettings, setPlatformSettings] = useState({});
  const [cms, setCms] = useState({});
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [rides, setRides] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [fareMatrix, setFareMatrix] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  // Smart driver matching
  const [matchedDrivers, setMatchedDrivers] = useState([]);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [reports, setReports] = useState([]);
  const [emergencyAlerts, setEmergencyAlerts] = useState([]);

  // Filters
  const [rideFilter, setRideFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");

  // Dialogs
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
  const [addDriverDialog, setAddDriverDialog] = useState(false);
  const [editDriverDialog, setEditDriverDialog] = useState({ open: false, driver: null });
  const [reviewDocsDialog, setReviewDocsDialog] = useState({ open: false, doc: null });
  const [editRideDialog, setEditRideDialog] = useState({ open: false, ride: null });
  const [reassignDialog, setReassignDialog] = useState({ open: false, rideId: "" });
  const [addDestinationDialog, setAddDestinationDialog] = useState(false);
  const [editDestinationDialog, setEditDestinationDialog] = useState({ open: false, dest: null });
  const [addFareDialog, setAddFareDialog] = useState(false);
  const [editFareDialog, setEditFareDialog] = useState({ open: false, fare: null });
  const [reportDialog, setReportDialog] = useState({ open: false, report: null });
  const [reportStatus, setReportStatus] = useState("");
  const [reportNotes, setReportNotes] = useState("");

  // Form states
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", password: "", vehicle_number: "", vehicle_type: "" });
  const [selectedDriver, setSelectedDriver] = useState("");
  const [newDestination, setNewDestination] = useState({ name: "", region: "" });
  const [newFare, setNewFare] = useState({ from_destination_id: "", to_destination_id: "", service_type: "", base_fare: "", fare_amount: "" });

  const headers = token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
  const fetchOpts = token ? { headers } : { headers, credentials: "include" };
  const fetchOptsRef = useRef(fetchOpts);
  fetchOptsRef.current = fetchOpts;

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/analytics`, fetchOptsRef.current);
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/branding`, fetchOptsRef.current);
      if (res.ok) setBranding(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchPlatformSettings = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/platform-settings`, fetchOptsRef.current);
      if (res.ok) setPlatformSettings(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchCms = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/cms`, fetchOptsRef.current);
      if (res.ok) setCms(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/users`, fetchOptsRef.current);
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/drivers`, fetchOptsRef.current);
      if (res.ok) setDrivers(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/documents`, fetchOptsRef.current);
      if (res.ok) setDocuments(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchRides = useCallback(async () => {
    try {
      const url = rideFilter === "all" ? `${API}/superadmin/rides` : `${API}/superadmin/rides?status=${rideFilter}`;
      const res = await safeFetch(url, fetchOpts);
      if (res.ok) setRides(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, [rideFilter]);

  const fetchDestinations = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/destinations`, fetchOptsRef.current);
      if (res.ok) setDestinations(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchFareMatrix = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/superadmin/fare-matrix`, fetchOptsRef.current);
      if (res.ok) setFareMatrix(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchServiceTypes = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/service-types`);
      if (res.ok) setServiceTypes(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/reports`, fetchOptsRef.current);
      if (res.ok) setReports(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  const fetchEmergencyAlerts = useCallback(async () => {
    try {
      const res = await safeFetch(`${API}/admin/emergency-alerts`, fetchOptsRef.current);
      if (res.ok) setEmergencyAlerts(await res.json());
    } catch (e) { console.error("Operation failed:", e); }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchBranding();
    fetchPlatformSettings();
    fetchCms();
    fetchUsers();
    fetchDrivers();
    fetchDocuments();
    fetchDestinations();
    fetchFareMatrix();
    fetchServiceTypes();
  }, []);

  useEffect(() => {
    fetchRides();
  }, [rideFilter, fetchRides]);

  useEffect(() => {
    if (activeTab === "reports") {
      fetchReports();
      fetchEmergencyAlerts();
    }
  }, [activeTab]);

  // Report handlers
  const handleUpdateReport = async () => {
    if (!reportDialog.report) return;
    try {
      const res = await safeFetch(`${API}/admin/reports/${reportDialog.report.report_id}`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status: reportStatus, admin_notes: reportNotes }),
      });
      if (res.ok) {
        toast.success("Report updated");
        setReportDialog({ open: false, report: null });
        fetchReports();
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleResolveEmergency = async (alertId) => {
    try {
      const res = await safeFetch(`${API}/admin/emergency-alerts/${alertId}`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ status: "resolved" }),
      });
      if (res.ok) {
        toast.success("Alert resolved");
        fetchEmergencyAlerts();
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  // Smart Driver Matching
  const fetchMatchedDrivers = async (rideId) => {
    setLoadingMatch(true);
    try {
      const res = await safeFetch(`${API}/admin/rides/${rideId}/match-drivers`, fetchOptsRef.current);
      if (res.ok) {
        const data = await res.json();
        setMatchedDrivers(data.matched_drivers || []);
      }
    } catch { setMatchedDrivers([]); }
    setLoadingMatch(false);
  };

  const openAssignDialog = (rideId) => {
    setReassignDialog({ open: true, rideId });
    setSelectedDriver("");
    setMatchedDrivers([]);
    fetchMatchedDrivers(rideId);
  };

  const handleAutoAssign = async () => {
    try {
      const res = await safeFetch(`${API}/admin/rides/${reassignDialog.rideId}/auto-assign`, { method: "POST", ...fetchOpts });
      if (res.ok) { 
        const data = await res.json();
        toast.success(`Auto-assigned to ${data.driver.name} (Score: ${data.score.total_score})`); 
        setReassignDialog({ open: false, rideId: "" }); 
        setMatchedDrivers([]); 
        fetchRides(); 
      } else toast.error((await res.json()).detail);
    } catch (e) { console.error(e); toast.error("Auto-assign failed"); }
  };

  // Branding handlers
  const handleSaveBranding = async () => {
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/branding`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(branding),
      });
      if (res.ok) toast.success("Branding updated successfully");
      else toast.error("Failed to update branding");
    } catch (e) { console.error(e); toast.error("Error saving branding"); }
    setLoading(false);
  };

  // Platform settings handlers
  const handleSavePlatformSettings = async () => {
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/platform-settings`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(platformSettings),
      });
      if (res.ok) {
        toast.success("Platform settings updated");
        // Refresh the settings to get the updated data
        fetchPlatformSettings();
      } else {
        toast.error("Failed to update settings");
      }
    } catch (e) { console.error(e); toast.error("Error saving settings"); }
    setLoading(false);
  };

  // CMS handlers
  const handleSaveCmsSection = async (section) => {
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/cms/${section}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(cms[section]),
      });
      if (res.ok) toast.success(`${section} content updated`);
      else toast.error("Failed to update content");
    } catch (e) { console.error(e); toast.error("Error saving content"); }
    setLoading(false);
  };

  // User handlers
  const handleUpdateUser = async () => {
    if (!editUserDialog.user) return;
    setLoading(true);
    try {
      const userData = { ...editUserDialog.user };
      const newPassword = userData.new_password;
      delete userData.new_password;
      
      // Update user data
      const res = await safeFetch(`${API}/superadmin/users/${editUserDialog.user.user_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(userData),
      });
      
      // Update role if changed
      const originalUser = users.find(u => u.user_id === editUserDialog.user.user_id);
      if (originalUser && originalUser.role !== userData.role) {
        await safeFetch(`${API}/superadmin/users/${editUserDialog.user.user_id}/role`, {
          method: "PUT", ...fetchOpts, body: JSON.stringify({ role: userData.role }),
        });
      }
      
      // Reset password if provided
      if (newPassword && newPassword.trim()) {
        await safeFetch(`${API}/superadmin/users/${editUserDialog.user.user_id}/reset-password`, {
          method: "PUT", ...fetchOpts, body: JSON.stringify({ new_password: newPassword }),
        });
      }
      
      if (res.ok) { toast.success("User updated successfully"); setEditUserDialog({ open: false, user: null }); fetchUsers(); fetchDrivers(); }
      else toast.error("Failed to update user");
    } catch (e) { console.error(e); toast.error("Error updating user"); }
    setLoading(false);
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const res = await safeFetch(`${API}/superadmin/users/${userId}/role`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) { toast.success(`Role changed to ${newRole}`); fetchUsers(); fetchDrivers(); }
      else toast.error("Failed to change role");
    } catch (e) { console.error(e); toast.error("Error changing role"); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const res = await safeFetch(`${API}/superadmin/users/${userId}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("User deleted"); fetchUsers(); fetchDrivers(); }
      else { const d = await res.json(); toast.error(d.detail); }
    } catch (e) { console.error(e); toast.error("Error deleting user"); }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt("Enter new password:");
    if (!newPassword) return;
    try {
      const res = await safeFetch(`${API}/superadmin/users/${userId}/reset-password`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ new_password: newPassword }),
      });
      if (res.ok) toast.success("Password reset successfully");
      else toast.error("Failed to reset password");
    } catch (e) { console.error(e); toast.error("Error resetting password"); }
  };

  const handleSuspendUser = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const res = await safeFetch(`${API}/superadmin/users/${userId}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ account_status: newStatus }),
      });
      if (res.ok) { toast.success(`User ${newStatus}`); fetchUsers(); }
    } catch (e) { console.error(e); toast.error("Error updating user status"); }
  };

  // Driver handlers
  const handleAddDriver = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/drivers`, {
        method: "POST", ...fetchOpts, body: JSON.stringify(newDriver),
      });
      if (res.ok) {
        toast.success("Driver added successfully");
        setAddDriverDialog(false);
        setNewDriver({ name: "", phone: "", password: "", vehicle_number: "", vehicle_type: "" });
        fetchDrivers();
      } else { const d = await res.json(); toast.error(d.detail); }
    } catch (e) { console.error(e); toast.error("Error adding driver"); }
    setLoading(false);
  };

  const handleUpdateDriver = async () => {
    if (!editDriverDialog.driver) return;
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/drivers/${editDriverDialog.driver.user_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(editDriverDialog.driver),
      });
      if (res.ok) { toast.success("Driver updated"); setEditDriverDialog({ open: false, driver: null }); fetchDrivers(); }
      else toast.error("Failed to update driver");
    } catch (e) { console.error(e); toast.error("Error updating driver"); }
    setLoading(false);
  };

  const handleDeleteDriver = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this driver?")) return;
    try {
      const res = await safeFetch(`${API}/superadmin/drivers/${userId}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Driver removed"); fetchDrivers(); fetchUsers(); }
    } catch (e) { console.error(e); toast.error("Error removing driver"); }
  };

  // Document review handlers
  const handleReviewDocuments = async (userId, status) => {
    try {
      const res = await safeFetch(`${API}/superadmin/documents/${userId}/review`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ approval_status: status }),
      });
      if (res.ok) {
        toast.success(`Documents ${status}`);
        setReviewDocsDialog({ open: false, doc: null });
        fetchDocuments();
        fetchDrivers();
      }
    } catch (e) { console.error(e); toast.error("Error reviewing documents"); }
  };

  // Ride handlers
  const handleUpdateRide = async () => {
    if (!editRideDialog.ride) return;
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/rides/${editRideDialog.ride.ride_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(editRideDialog.ride),
      });
      if (res.ok) { toast.success("Ride updated"); setEditRideDialog({ open: false, ride: null }); fetchRides(); }
      else toast.error("Failed to update ride");
    } catch (e) { console.error(e); toast.error("Error updating ride"); }
    setLoading(false);
  };

  const handleReassignDriver = async () => {
    if (!selectedDriver) { toast.error("Select a driver"); return; }
    try {
      const res = await safeFetch(`${API}/superadmin/rides/${reassignDialog.rideId}/reassign`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ driver_id: selectedDriver }),
      });
      if (res.ok) { toast.success("Driver reassigned"); setReassignDialog({ open: false, rideId: "" }); fetchRides(); }
    } catch (e) { console.error(e); toast.error("Error reassigning driver"); }
  };

  const handleOverrideCommission = async (rideId) => {
    const newCommission = prompt("Enter new commission amount:");
    if (!newCommission) return;
    try {
      const res = await safeFetch(`${API}/superadmin/rides/${rideId}/override-commission`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({ commission_amount: parseFloat(newCommission) }),
      });
      if (res.ok) { toast.success("Commission overridden"); fetchRides(); }
    } catch (e) { console.error(e); toast.error("Error overriding commission"); }
  };

  // Destination handlers
  const handleAddDestination = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/destinations`, {
        method: "POST", ...fetchOpts, body: JSON.stringify(newDestination),
      });
      if (res.ok) {
        toast.success("Destination added");
        setAddDestinationDialog(false);
        setNewDestination({ name: "", region: "" });
        fetchDestinations();
      } else toast.error("Failed to add destination");
    } catch (e) { console.error(e); toast.error("Error adding destination"); }
    setLoading(false);
  };

  const handleUpdateDestination = async () => {
    if (!editDestinationDialog.dest) return;
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/destinations/${editDestinationDialog.dest.destination_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify(editDestinationDialog.dest),
      });
      if (res.ok) { toast.success("Destination updated"); setEditDestinationDialog({ open: false, dest: null }); fetchDestinations(); }
    } catch (e) { console.error(e); toast.error("Error updating destination"); }
    setLoading(false);
  };

  const handleDeleteDestination = async (destId) => {
    if (!window.confirm("Delete this destination?")) return;
    try {
      const res = await safeFetch(`${API}/superadmin/destinations/${destId}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Destination deleted"); fetchDestinations(); }
    } catch (e) { console.error(e); toast.error("Error deleting destination"); }
  };

  // Fare Matrix handlers
  const handleAddFare = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/fare-matrix`, {
        method: "POST", ...fetchOpts, body: JSON.stringify({
          ...newFare,
          base_fare: parseFloat(newFare.base_fare),
          fare_amount: parseFloat(newFare.fare_amount),
        }),
      });
      if (res.ok) {
        toast.success("Fare entry added");
        setAddFareDialog(false);
        setNewFare({ from_destination_id: "", to_destination_id: "", service_type: "", base_fare: "", fare_amount: "" });
        fetchFareMatrix();
      } else toast.error("Failed to add fare");
    } catch (e) { console.error(e); toast.error("Error adding fare"); }
    setLoading(false);
  };

  const handleUpdateFare = async () => {
    if (!editFareDialog.fare) return;
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/superadmin/fare-matrix/${editFareDialog.fare.fare_id}`, {
        method: "PUT", ...fetchOpts, body: JSON.stringify({
          base_fare: parseFloat(editFareDialog.fare.base_fare),
          fare_amount: parseFloat(editFareDialog.fare.fare_amount),
        }),
      });
      if (res.ok) { toast.success("Fare updated"); setEditFareDialog({ open: false, fare: null }); fetchFareMatrix(); }
    } catch (e) { console.error(e); toast.error("Error updating fare"); }
    setLoading(false);
  };

  const handleDeleteFare = async (fareId) => {
    if (!window.confirm("Delete this fare entry?")) return;
    try {
      const res = await safeFetch(`${API}/superadmin/fare-matrix/${fareId}`, { method: "DELETE", ...fetchOpts });
      if (res.ok) { toast.success("Fare deleted"); fetchFareMatrix(); }
    } catch (e) { console.error(e); toast.error("Error deleting fare"); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  // Filtered data
  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.phone?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredDrivers = drivers.filter(d =>
    d.name?.toLowerCase().includes(driverSearch.toLowerCase()) ||
    d.phone?.toLowerCase().includes(driverSearch.toLowerCase()) ||
    d.vehicle_number?.toLowerCase().includes(driverSearch.toLowerCase())
  );

  const pendingDocs = documents.filter(d => d.approval_status === "pending");

  return (
    <div className="min-h-screen bg-slate-50 font-body flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-72 bg-slate-900 transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber to-amber-light flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white">Super Admin</h1>
              <p className="text-xs text-slate-400">Full Platform Control</p>
            </div>
          </div>
        </div>
        
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]">
          {sidebarItems.map((item) => (
            <button
              key={item.key}
              data-testid={`sa-sidebar-${item.key}`}
              onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm transition-all ${
                activeTab === item.key
                  ? "bg-amber/20 text-amber"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">{item.label}</p>
                <p className="text-xs opacity-60">{item.desc}</p>
              </div>
            </button>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
              <Shield className="w-4 h-4 text-amber" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="sa-logout-btn"
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-5 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="lg:hidden text-slate-600" onClick={() => setSidebarOpen(!sidebarOpen)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h2 className="font-heading font-semibold text-lg text-slate-900 capitalize">
                  {sidebarItems.find(i => i.key === activeTab)?.label}
                </h2>
                <p className="text-xs text-slate-500">{sidebarItems.find(i => i.key === activeTab)?.desc}</p>
              </div>
            </div>
            {pendingDocs.length > 0 && (
              <Badge className="bg-red-100 text-red-700 rounded-full">
                {pendingDocs.length} Pending Reviews
              </Badge>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div data-testid="sa-overview" className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Total Revenue", value: `₹${analytics.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: "bg-emerald-500", trend: "+12%" },
                  { label: "Commission Earned", value: `₹${analytics.total_commission?.toLocaleString() || 0}`, icon: TrendingUp, color: "bg-amber-500", trend: "+8%" },
                  { label: "Driver Payouts", value: `₹${analytics.total_driver_pay?.toLocaleString() || 0}`, icon: Car, color: "bg-blue-500", trend: "" },
                  { label: "Total Rides", value: analytics.total_rides || 0, icon: Route, color: "bg-purple-500", trend: "" },
                ].map((m, i) => (
                  <Card key={i} data-testid={`sa-stat-${i}`} className="rounded-2xl border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-10 h-10 rounded-xl ${m.color} flex items-center justify-center`}>
                          <m.icon className="w-5 h-5 text-white" />
                        </div>
                        {m.trend && <span className="text-xs text-emerald-600 font-medium">{m.trend}</span>}
                      </div>
                      <p className="text-sm text-slate-500">{m.label}</p>
                      <p className="font-heading font-bold text-2xl text-slate-900">{m.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: "Total Users", value: analytics.total_users || 0, icon: Users },
                  { label: "Total Drivers", value: analytics.total_drivers || 0, icon: Car },
                  { label: "Active Drivers", value: analytics.active_drivers || 0, icon: Activity },
                  { label: "Completed Rides", value: analytics.completed_rides || 0, icon: CheckCircle },
                  { label: "Pending Rides", value: analytics.pending_rides || 0, icon: Clock },
                  { label: "Pending Docs", value: analytics.pending_documents || 0, icon: FileCheck },
                ].map((m, i) => (
                  <Card key={i} className="rounded-xl border-0 shadow-sm">
                    <CardContent className="p-4 text-center">
                      <m.icon className="w-5 h-5 mx-auto mb-2 text-slate-400" />
                      <p className="font-heading font-bold text-xl text-slate-900">{m.value}</p>
                      <p className="text-xs text-slate-500">{m.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quick Actions */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button data-testid="sa-quick-users" onClick={() => setActiveTab("users")} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                    <Users className="w-4 h-4 mr-2" /> Manage Users
                  </Button>
                  <Button data-testid="sa-quick-drivers" onClick={() => setActiveTab("drivers")} className="bg-amber hover:bg-amber-dark text-white rounded-full">
                    <Car className="w-4 h-4 mr-2" /> Manage Drivers
                  </Button>
                  <Button data-testid="sa-quick-rides" onClick={() => setActiveTab("rides")} variant="outline" className="rounded-full">
                    <Route className="w-4 h-4 mr-2" /> View All Rides
                  </Button>
                  <Button data-testid="sa-quick-cms" onClick={() => setActiveTab("cms")} variant="outline" className="rounded-full">
                    <FileText className="w-4 h-4 mr-2" /> Edit Content
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* BRANDING TAB */}
          {activeTab === "branding" && (
            <div data-testid="sa-branding" className="max-w-2xl space-y-6">
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-amber" /> Brand Identity
                  </CardTitle>
                  <CardDescription>Customize your platform's visual identity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-slate-700">Logo URL</Label>
                    <div className="flex gap-3 mt-2">
                      <Input
                        data-testid="branding-logo-url"
                        value={branding.logo_url || ""}
                        onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                        placeholder="https://example.com/logo.png"
                        className="rounded-xl"
                      />
                      {branding.logo_url && (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                          <img src={branding.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Primary Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          data-testid="branding-primary-color"
                          type="color"
                          value={branding.primary_color || "#1B4332"}
                          onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                          className="w-12 h-10 p-1 rounded-lg cursor-pointer"
                        />
                        <Input
                          value={branding.primary_color || "#1B4332"}
                          onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                          className="rounded-xl font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Secondary Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          data-testid="branding-secondary-color"
                          type="color"
                          value={branding.secondary_color || "#D4A017"}
                          onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                          className="w-12 h-10 p-1 rounded-lg cursor-pointer"
                        />
                        <Input
                          value={branding.secondary_color || "#D4A017"}
                          onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                          className="rounded-xl font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Accent Color</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          data-testid="branding-accent-color"
                          type="color"
                          value={branding.accent_color || "#E9C46A"}
                          onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                          className="w-12 h-10 p-1 rounded-lg cursor-pointer"
                        />
                        <Input
                          value={branding.accent_color || "#E9C46A"}
                          onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                          className="rounded-xl font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Font Family</Label>
                      <Select
                        value={branding.font_family || "Plus Jakarta Sans"}
                        onValueChange={(v) => setBranding({ ...branding, font_family: v })}
                      >
                        <SelectTrigger data-testid="branding-font" className="mt-2 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Plus Jakarta Sans">Plus Jakarta Sans</SelectItem>
                          <SelectItem value="Inter">Inter</SelectItem>
                          <SelectItem value="Poppins">Poppins</SelectItem>
                          <SelectItem value="Manrope">Manrope</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    data-testid="save-branding-btn"
                    onClick={handleSaveBranding}
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save Branding
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* PLATFORM SETTINGS TAB */}
          {activeTab === "platform" && (
            <div data-testid="sa-platform" className="max-w-2xl space-y-6">
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5 text-amber" /> Platform Configuration
                  </CardTitle>
                  <CardDescription>Core settings for your platform</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><Globe className="w-4 h-4" /> Platform Name</Label>
                    <Input
                      data-testid="platform-name"
                      value={platformSettings.platform_name || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, platform_name: e.target.value })}
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 flex items-center gap-2"><Mail className="w-4 h-4" /> Support Email</Label>
                      <Input
                        data-testid="platform-email"
                        type="email"
                        value={platformSettings.support_email || ""}
                        onChange={(e) => setPlatformSettings({ ...platformSettings, support_email: e.target.value })}
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 flex items-center gap-2"><Phone className="w-4 h-4" /> Support Phone</Label>
                      <Input
                        data-testid="platform-phone"
                        value={platformSettings.support_phone || ""}
                        onChange={(e) => setPlatformSettings({ ...platformSettings, support_phone: e.target.value })}
                        className="mt-2 rounded-xl"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><MapPin className="w-4 h-4" /> Business Address</Label>
                    <Input
                      data-testid="platform-address"
                      value={platformSettings.business_address || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, business_address: e.target.value })}
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Commission Per Ride (₹)</Label>
                    <Input
                      data-testid="platform-commission"
                      type="number"
                      value={platformSettings.commission_amount || 100}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, commission_amount: e.target.value })}
                      className="mt-2 rounded-xl"
                    />
                  </div>

                  <Button
                    data-testid="save-platform-btn"
                    onClick={handleSavePlatformSettings}
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Social Links Card */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-pink-500" /> Social Media Links
                  </CardTitle>
                  <CardDescription>Connect your social media accounts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><Instagram className="w-4 h-4 text-pink-500" /> Instagram URL</Label>
                    <Input
                      data-testid="platform-instagram"
                      value={platformSettings.instagram_url || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/cabvalley"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><Facebook className="w-4 h-4 text-blue-600" /> Facebook URL</Label>
                    <Input
                      data-testid="platform-facebook"
                      value={platformSettings.facebook_url || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, facebook_url: e.target.value })}
                      placeholder="https://facebook.com/cabvalley"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp Number</Label>
                    <Input
                      data-testid="platform-whatsapp"
                      value={platformSettings.whatsapp_number || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, whatsapp_number: e.target.value })}
                      placeholder="+91 9876543210"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><Twitter className="w-4 h-4 text-sky-500" /> Twitter/X URL</Label>
                    <Input
                      data-testid="platform-twitter"
                      value={platformSettings.twitter_url || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, twitter_url: e.target.value })}
                      placeholder="https://twitter.com/cabvalley"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <Button
                    onClick={handleSavePlatformSettings}
                    disabled={loading}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save Social Links
                  </Button>
                </CardContent>
              </Card>

              {/* UPI Payment Settings Card */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" /> UPI Payment Settings
                  </CardTitle>
                  <CardDescription>Configure UPI payment details for wallet recharges</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><Wallet className="w-4 h-4" /> UPI ID</Label>
                    <Input
                      data-testid="platform-upi-id"
                      value={platformSettings.upi_id || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, upi_id: e.target.value })}
                      placeholder="cabvalley@paytm"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><QrCode className="w-4 h-4" /> UPI QR Code URL</Label>
                    <Input
                      data-testid="platform-upi-qr"
                      value={platformSettings.upi_qr_url || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, upi_qr_url: e.target.value })}
                      placeholder="https://example.com/qrcode.png"
                      className="mt-2 rounded-xl"
                    />
                    {platformSettings.upi_qr_url && (
                      <div className="mt-3 flex justify-center">
                        <img src={platformSettings.upi_qr_url} alt="UPI QR Code" className="w-32 h-32 rounded-xl border border-slate-200 object-contain" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center gap-2"><IndianRupee className="w-4 h-4" /> Merchant Name</Label>
                    <Input
                      data-testid="platform-merchant-name"
                      value={platformSettings.upi_merchant_name || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, upi_merchant_name: e.target.value })}
                      placeholder="Cab Valley Travels"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <Button
                    onClick={handleSavePlatformSettings}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save UPI Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Commission Settings Card */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Percent className="w-5 h-5 text-amber" /> Commission Settings
                  </CardTitle>
                  <CardDescription>Configure platform commission rates</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Fixed Commission (₹)</Label>
                      <Input
                        data-testid="platform-fixed-commission"
                        type="number"
                        value={platformSettings.commission_amount || 100}
                        onChange={(e) => setPlatformSettings({ ...platformSettings, commission_amount: e.target.value })}
                        className="mt-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Commission Percentage (%)</Label>
                      <Input
                        data-testid="platform-commission-percent"
                        type="number"
                        min="0"
                        max="100"
                        value={platformSettings.commission_percentage || 0}
                        onChange={(e) => setPlatformSettings({ ...platformSettings, commission_percentage: e.target.value })}
                        className="mt-2 rounded-xl"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-xl">
                    Commission is calculated as: Fixed Amount + (Fare × Percentage/100). 
                    Set percentage to 0 for fixed commission only.
                  </p>
                  <Button
                    onClick={handleSavePlatformSettings}
                    disabled={loading}
                    className="w-full bg-amber hover:bg-amber/90 text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" /> Save Commission Settings
                  </Button>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Legal Documents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Privacy Policy URL</Label>
                    <Input
                      data-testid="platform-privacy"
                      value={platformSettings.privacy_policy || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, privacy_policy: e.target.value })}
                      placeholder="https://example.com/privacy"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Terms of Service URL</Label>
                    <Input
                      data-testid="platform-terms"
                      value={platformSettings.terms_of_service || ""}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, terms_of_service: e.target.value })}
                      placeholder="https://example.com/terms"
                      className="mt-2 rounded-xl"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* FARES & ROUTES TAB */}
          {activeTab === "fares" && (
            <div data-testid="sa-fares" className="space-y-6">
              {/* Destinations Section */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-amber" /> Destinations
                    </CardTitle>
                    <CardDescription>Manage available pickup and drop locations</CardDescription>
                  </div>
                  <Button data-testid="add-destination-btn" onClick={() => setAddDestinationDialog(true)} className="bg-amber hover:bg-amber-dark text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Destination
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-slate-600 text-xs uppercase">Name</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Region</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Status</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {destinations.map((dest) => (
                        <TableRow key={dest.destination_id}>
                          <TableCell className="font-medium text-slate-900">{dest.name}</TableCell>
                          <TableCell className="text-slate-600">{dest.region}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-full text-xs ${dest.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                              {dest.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditDestinationDialog({ open: true, dest: { ...dest } })}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteDestination(dest.destination_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {destinations.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-slate-400">No destinations added</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Fare Matrix Section */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber" /> Fare Matrix
                    </CardTitle>
                    <CardDescription>Set fares between destinations for each service type</CardDescription>
                  </div>
                  <Button data-testid="add-fare-btn" onClick={() => setAddFareDialog(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Fare
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-slate-600 text-xs uppercase">From</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">To</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Service</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Base Fare</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Final Fare</TableHead>
                        <TableHead className="text-slate-600 text-xs uppercase">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fareMatrix.map((fare) => {
                        const fromDest = destinations.find(d => d.destination_id === fare.from_destination_id);
                        const toDest = destinations.find(d => d.destination_id === fare.to_destination_id);
                        return (
                          <TableRow key={fare.fare_id}>
                            <TableCell className="font-medium text-slate-900">{fromDest?.name || fare.from_destination_id}</TableCell>
                            <TableCell className="font-medium text-slate-900">{toDest?.name || fare.to_destination_id}</TableCell>
                            <TableCell className="text-slate-600 capitalize">{fare.service_type.replace(/_/g, " ")}</TableCell>
                            <TableCell className="text-slate-600">₹{fare.base_fare}</TableCell>
                            <TableCell className="font-bold text-amber">₹{fare.fare_amount}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditFareDialog({ open: true, fare: { ...fare } })}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDeleteFare(fare.fare_id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {fareMatrix.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-slate-400">No fare entries added</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CMS TAB */}
          {activeTab === "cms" && (
            <div data-testid="sa-cms" className="space-y-6">
              <Tabs defaultValue="hero" className="w-full">
                <TabsList className="bg-white border rounded-xl p-1 mb-6">
                  <TabsTrigger value="hero" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Hero Section</TabsTrigger>
                  <TabsTrigger value="footer" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Footer</TabsTrigger>
                  <TabsTrigger value="about" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">About</TabsTrigger>
                  <TabsTrigger value="driver_recruitment" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Driver CTA</TabsTrigger>
                </TabsList>

                <TabsContent value="hero">
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Hero Section Content</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Badge Text</Label>
                        <Input
                          data-testid="cms-hero-badge"
                          value={cms.hero?.badge_text || ""}
                          onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, badge_text: e.target.value } })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Title Line 1</Label>
                          <Input
                            data-testid="cms-hero-title1"
                            value={cms.hero?.title_line1 || ""}
                            onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, title_line1: e.target.value } })}
                            className="mt-2 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label>Title Line 2</Label>
                          <Input
                            data-testid="cms-hero-title2"
                            value={cms.hero?.title_line2 || ""}
                            onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, title_line2: e.target.value } })}
                            className="mt-2 rounded-xl"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Subtitle</Label>
                        <Textarea
                          data-testid="cms-hero-subtitle"
                          value={cms.hero?.subtitle || ""}
                          onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, subtitle: e.target.value } })}
                          className="mt-2 rounded-xl"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Primary CTA</Label>
                          <Input
                            data-testid="cms-hero-cta1"
                            value={cms.hero?.cta_primary || ""}
                            onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, cta_primary: e.target.value } })}
                            className="mt-2 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label>Secondary CTA</Label>
                          <Input
                            data-testid="cms-hero-cta2"
                            value={cms.hero?.cta_secondary || ""}
                            onChange={(e) => setCms({ ...cms, hero: { ...cms.hero, cta_secondary: e.target.value } })}
                            className="mt-2 rounded-xl"
                          />
                        </div>
                      </div>
                      <Button
                        data-testid="save-cms-hero"
                        onClick={() => handleSaveCmsSection("hero")}
                        disabled={loading}
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                      >
                        <Save className="w-4 h-4 mr-2" /> Save Hero Content
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="footer">
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Footer Content</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Tagline</Label>
                        <Input
                          data-testid="cms-footer-tagline"
                          value={cms.footer?.tagline || ""}
                          onChange={(e) => setCms({ ...cms, footer: { ...cms.footer, tagline: e.target.value } })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          data-testid="cms-footer-desc"
                          value={cms.footer?.description || ""}
                          onChange={(e) => setCms({ ...cms, footer: { ...cms.footer, description: e.target.value } })}
                          className="mt-2 rounded-xl"
                          rows={3}
                        />
                      </div>
                      <Button
                        data-testid="save-cms-footer"
                        onClick={() => handleSaveCmsSection("footer")}
                        disabled={loading}
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                      >
                        <Save className="w-4 h-4 mr-2" /> Save Footer Content
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="about">
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">About Section</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          data-testid="cms-about-title"
                          value={cms.about?.title || ""}
                          onChange={(e) => setCms({ ...cms, about: { ...cms.about, title: e.target.value } })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Text</Label>
                        <Textarea
                          data-testid="cms-about-text"
                          value={cms.about?.text || ""}
                          onChange={(e) => setCms({ ...cms, about: { ...cms.about, text: e.target.value } })}
                          className="mt-2 rounded-xl"
                          rows={4}
                        />
                      </div>
                      <Button
                        data-testid="save-cms-about"
                        onClick={() => handleSaveCmsSection("about")}
                        disabled={loading}
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                      >
                        <Save className="w-4 h-4 mr-2" /> Save About Content
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="driver_recruitment">
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Driver Recruitment CTA</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          data-testid="cms-driver-title"
                          value={cms.driver_recruitment?.title || ""}
                          onChange={(e) => setCms({ ...cms, driver_recruitment: { ...cms.driver_recruitment, title: e.target.value } })}
                          className="mt-2 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Text</Label>
                        <Textarea
                          data-testid="cms-driver-text"
                          value={cms.driver_recruitment?.text || ""}
                          onChange={(e) => setCms({ ...cms, driver_recruitment: { ...cms.driver_recruitment, text: e.target.value } })}
                          className="mt-2 rounded-xl"
                          rows={4}
                        />
                      </div>
                      <Button
                        data-testid="save-cms-driver"
                        onClick={() => handleSaveCmsSection("driver_recruitment")}
                        disabled={loading}
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                      >
                        <Save className="w-4 h-4 mr-2" /> Save Driver CTA Content
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* DOCUMENTS TAB */}
          {activeTab === "documents" && (
            <DocumentManagement isSuperAdmin={true} />
          )}

          {/* SERVICES TAB */}
          {activeTab === "services" && (
            <ServiceManagement token={token} />
          )}

          {/* PAYMENTS TAB */}
          {activeTab === "payments" && (
            <PaymentManagement token={token} isSuperAdmin={true} />
          )}

          {/* USERS TAB */}
          {activeTab === "users" && (
            <div data-testid="sa-users" className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="user-search"
                    placeholder="Search users by name, email, or phone..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
                <Badge className="bg-slate-100 text-slate-700 rounded-full self-start">
                  {filteredUsers.length} users
                </Badge>
              </div>

              <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">User</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Contact</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Role</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Status</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Joined</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.user_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                              ) : (
                                <span className="text-sm font-medium text-slate-600">{u.name?.[0]?.toUpperCase()}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.name}</p>
                              <p className="text-xs text-slate-500 font-mono">{u.user_id.slice(-8)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-700">{u.email || "-"}</p>
                          <p className="text-xs text-slate-500">{u.phone || "-"}</p>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u.role}
                            onValueChange={(v) => handleChangeRole(u.user_id, v)}
                            disabled={u.role === "super_admin"}
                          >
                            <SelectTrigger className="w-28 h-8 rounded-lg text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="customer">Customer</SelectItem>
                              <SelectItem value="driver">Driver</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-full text-xs ${u.account_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {u.account_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              data-testid={`edit-user-${u.user_id}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => setEditUserDialog({ open: true, user: { ...u } })}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {u.role !== "super_admin" && (
                              <>
                                <Button
                                  data-testid={`suspend-user-${u.user_id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleSuspendUser(u.user_id, u.account_status)}
                                >
                                  {u.account_status === "active" ? <Ban className="w-4 h-4 text-orange-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                                </Button>
                                <Button
                                  data-testid={`reset-pwd-${u.user_id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleResetPassword(u.user_id)}
                                >
                                  <RefreshCw className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button
                                  data-testid={`delete-user-${u.user_id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleDeleteUser(u.user_id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">No users found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* DRIVERS TAB */}
          {activeTab === "drivers" && (
            <div data-testid="sa-drivers" className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="driver-search"
                    placeholder="Search drivers..."
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
                <div className="flex gap-2">
                  {pendingDocs.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 rounded-full cursor-pointer" onClick={() => setReviewDocsDialog({ open: true, doc: pendingDocs[0] })}>
                      {pendingDocs.length} Pending Reviews
                    </Badge>
                  )}
                  <Button data-testid="add-driver-btn" onClick={() => setAddDriverDialog(true)} className="bg-amber hover:bg-amber-dark text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Add Driver
                  </Button>
                </div>
              </div>

              <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Driver</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Vehicle</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Status</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Documents</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Earnings</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Rating</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDrivers.map((d) => (
                      <TableRow key={d.driver_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              <Car className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{d.name}</p>
                              <p className="text-xs text-slate-500">{d.phone}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-700">{d.vehicle_type}</p>
                          <p className="text-xs text-slate-500 font-mono">{d.vehicle_number}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-full text-xs ${d.status === "online" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                            {d.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {d.documents ? (
                            <Badge
                              className={`rounded-full text-xs cursor-pointer ${
                                d.documents.approval_status === "approved" ? "bg-emerald-100 text-emerald-700" :
                                d.documents.approval_status === "rejected" ? "bg-red-100 text-red-700" :
                                "bg-amber-100 text-amber-700"
                              }`}
                              onClick={() => setReviewDocsDialog({ open: true, doc: d.documents })}
                            >
                              {d.documents.approval_status}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">No docs</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">₹{d.total_earnings?.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber fill-amber" />
                            <span className="font-medium">{d.rating}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              data-testid={`edit-driver-${d.user_id}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => setEditDriverDialog({ open: true, driver: { ...d } })}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-driver-${d.user_id}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDeleteDriver(d.user_id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredDrivers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-400">No drivers found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* RIDES TAB */}
          {activeTab === "rides" && (
            <div data-testid="sa-rides" className="space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                {["all", "requested", "assigned", "accepted", "started", "completed", "cancelled"].map((f) => (
                  <Button
                    key={f}
                    data-testid={`sa-ride-filter-${f}`}
                    variant={rideFilter === f ? "default" : "outline"}
                    onClick={() => setRideFilter(f)}
                    className={`rounded-full text-xs capitalize ${rideFilter === f ? "bg-slate-900 text-white" : ""}`}
                    size="sm"
                  >
                    {f}
                  </Button>
                ))}
              </div>

              <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Ride ID</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Customer</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Route</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Service</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Fare</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Commission</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Status</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Driver</TableHead>
                      <TableHead className="text-slate-600 text-xs uppercase font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rides.map((ride) => (
                      <TableRow key={ride.ride_id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-xs text-slate-500">{ride.ride_id.slice(-8)}</TableCell>
                        <TableCell className="text-sm text-slate-900">
                          {ride.customer_name}
                          {ride.passenger_count > 1 && (
                            <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{ride.passenger_count} pax</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p className="text-slate-700">{ride.pickup_location}</p>
                            <p className="text-slate-400">{ride.drop_location}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{ride.service_type.replace(/_/g, " ")}</TableCell>
                        <TableCell className="font-medium text-slate-900">₹{ride.fare_amount}</TableCell>
                        <TableCell className="text-amber font-medium">₹{ride.commission_amount}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-full text-xs ${statusColors[ride.ride_status]}`}>{ride.ride_status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{ride.driver_name || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              data-testid={`edit-ride-${ride.ride_id}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => setEditRideDialog({ open: true, ride: { ...ride } })}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {!["completed", "cancelled"].includes(ride.ride_status) && (
                              <>
                                <Button
                                  data-testid={`reassign-${ride.ride_id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => openAssignDialog(ride.ride_id)}
                                >
                                  <RefreshCw className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button
                                  data-testid={`override-commission-${ride.ride_id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleOverrideCommission(ride.ride_id)}
                                >
                                  <DollarSign className="w-4 h-4 text-amber" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rides.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-400">No rides found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-forest" />
                Analytics Dashboard
              </h2>
              <AnalyticsDashboard token={token} />
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === "reports" && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
                <AlertTriangle className="w-7 h-7 text-red-500" />
                Reports & Issues
              </h2>

              {/* Emergency Alerts */}
              {emergencyAlerts.filter(a => a.status === "active").length > 0 && (
                <Card className="rounded-2xl border-2 border-red-300 shadow-sm bg-red-50">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-red-700 flex items-center gap-2">
                      🚨 Active Emergency Alerts ({emergencyAlerts.filter(a => a.status === "active").length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emergencyAlerts.filter(a => a.status === "active").map((alert) => (
                      <div key={alert.alert_id} className="bg-white rounded-xl p-4 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-red-800">{alert.user_name} ({alert.user_phone})</p>
                            <p className="text-sm text-red-600">Location: {alert.location}</p>
                            {alert.ride_id && <p className="text-xs text-red-500">Ride: {alert.ride_id}</p>}
                            <p className="text-xs text-red-400 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                          </div>
                          <Button
                            data-testid={`resolve-alert-${alert.alert_id}`}
                            onClick={() => handleResolveEmergency(alert.alert_id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Resolved
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* User Reports */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">User Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No reports submitted yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Report ID</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Reporter</TableHead>
                          <TableHead>Reported</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.map((report) => (
                          <TableRow key={report.report_id} data-testid={`report-row-${report.report_id}`}>
                            <TableCell className="font-mono text-xs">{report.report_id.slice(-8)}</TableCell>
                            <TableCell>
                              <Badge className={`${report.report_type === "misbehavior" ? "bg-red-100 text-red-700" : report.report_type === "safety" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"} rounded-full text-xs`}>
                                {report.report_type}
                              </Badge>
                            </TableCell>
                            <TableCell>{report.reporter_name} <span className="text-xs text-slate-400">({report.reporter_role})</span></TableCell>
                            <TableCell>{report.reported_user_name}</TableCell>
                            <TableCell><Badge className={`${reportStatusColors[report.status]} rounded-full text-xs`}>{report.status}</Badge></TableCell>
                            <TableCell className="text-xs text-slate-500">{new Date(report.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`view-report-${report.report_id}`}
                                onClick={() => {
                                  setReportDialog({ open: true, report });
                                  setReportStatus(report.status);
                                  setReportNotes(report.admin_notes || "");
                                }}
                                className="rounded-full text-xs"
                              >
                                <Eye className="w-3 h-3 mr-1" /> View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Report Details Dialog */}
      <Dialog open={reportDialog.open} onOpenChange={(o) => setReportDialog({ ...reportDialog, open: o })}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Report Details</DialogTitle>
            <DialogDescription>Review and update report status</DialogDescription>
          </DialogHeader>
          {reportDialog.report && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Report ID:</span><p className="font-mono">{reportDialog.report.report_id}</p></div>
                <div><span className="text-slate-500">Ride ID:</span><p className="font-mono">{reportDialog.report.ride_id}</p></div>
                <div><span className="text-slate-500">Reporter:</span><p>{reportDialog.report.reporter_name} ({reportDialog.report.reporter_role})</p></div>
                <div><span className="text-slate-500">Reported:</span><p>{reportDialog.report.reported_user_name}</p></div>
              </div>
              <div>
                <span className="text-slate-500 text-sm">Type:</span>
                <Badge className={`ml-2 ${reportDialog.report.report_type === "misbehavior" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"} rounded-full`}>
                  {reportDialog.report.report_type}
                </Badge>
              </div>
              <div>
                <span className="text-slate-500 text-sm">Description:</span>
                <p className="mt-1 p-3 bg-slate-50 rounded-lg text-sm">{reportDialog.report.description}</p>
              </div>
              <div>
                <Label className="text-slate-700">Status</Label>
                <Select value={reportStatus} onValueChange={setReportStatus}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Admin Notes</Label>
                <Textarea
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  placeholder="Add notes about the investigation..."
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialog({ open: false, report: null })} className="rounded-xl">Cancel</Button>
            <Button onClick={handleUpdateReport} className="bg-forest hover:bg-forest-dark text-white rounded-xl">Update Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog.open} onOpenChange={(o) => setEditUserDialog({ ...editUserDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Full user management - update all fields</DialogDescription>
          </DialogHeader>
          {editUserDialog.user && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    data-testid="edit-user-name"
                    value={editUserDialog.user.name || ""}
                    onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, name: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    data-testid="edit-user-phone"
                    value={editUserDialog.user.phone || ""}
                    onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, phone: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="edit-user-email"
                  value={editUserDialog.user.email || ""}
                  onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, email: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  data-testid="edit-user-address"
                  value={editUserDialog.user.address || ""}
                  onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, address: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Profile Photo URL</Label>
                <Input
                  data-testid="edit-user-photo"
                  value={editUserDialog.user.profile_photo || ""}
                  onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, profile_photo: e.target.value } })}
                  placeholder="https://..."
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select 
                    value={editUserDialog.user.role || "customer"} 
                    onValueChange={(v) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, role: v } })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Account Status</Label>
                  <Select 
                    value={editUserDialog.user.account_status || "active"} 
                    onValueChange={(v) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, account_status: v } })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Wallet Balance (₹)</Label>
                <Input
                  type="number"
                  data-testid="edit-user-wallet"
                  value={editUserDialog.user.wallet_balance || 0}
                  onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, wallet_balance: parseFloat(e.target.value) || 0 } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="pt-2 border-t">
                <Label className="text-sm text-slate-500">Reset Password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="password"
                    placeholder="New password (leave empty to keep current)"
                    value={editUserDialog.user.new_password || ""}
                    onChange={(e) => setEditUserDialog({ ...editUserDialog, user: { ...editUserDialog.user, new_password: e.target.value } })}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog({ open: false, user: null })} className="rounded-xl">Cancel</Button>
            <Button data-testid="save-user-btn" onClick={handleUpdateUser} disabled={loading} className="bg-slate-900 text-white rounded-xl">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={addDriverDialog} onOpenChange={setAddDriverDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Driver</DialogTitle>
            <DialogDescription>Create a new driver account</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDriver} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                data-testid="new-driver-name"
                value={newDriver.name}
                onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="new-driver-phone"
                value={newDriver.phone}
                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                data-testid="new-driver-password"
                type="password"
                value={newDriver.password}
                onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>Vehicle Number</Label>
              <Input
                data-testid="new-driver-vehicle-number"
                value={newDriver.vehicle_number}
                onChange={(e) => setNewDriver({ ...newDriver, vehicle_number: e.target.value })}
                required
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>Vehicle Type</Label>
              <Input
                data-testid="new-driver-vehicle-type"
                value={newDriver.vehicle_type}
                onChange={(e) => setNewDriver({ ...newDriver, vehicle_type: e.target.value })}
                required
                placeholder="e.g., Sedan, SUV"
                className="mt-1 rounded-xl"
              />
            </div>
            <Button data-testid="submit-add-driver" type="submit" disabled={loading} className="w-full bg-amber hover:bg-amber-dark text-white rounded-xl">
              Add Driver
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={editDriverDialog.open} onOpenChange={(o) => setEditDriverDialog({ ...editDriverDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Driver</DialogTitle>
            <DialogDescription>Full driver management - update all fields</DialogDescription>
          </DialogHeader>
          {editDriverDialog.driver && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    data-testid="edit-driver-name"
                    value={editDriverDialog.driver.name || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, name: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    data-testid="edit-driver-phone"
                    value={editDriverDialog.driver.phone || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, phone: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="edit-driver-email"
                  value={editDriverDialog.driver.email || ""}
                  onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, email: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  data-testid="edit-driver-address"
                  value={editDriverDialog.driver.address || ""}
                  onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, address: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vehicle Number</Label>
                  <Input
                    data-testid="edit-driver-vehicle-number"
                    value={editDriverDialog.driver.vehicle_number || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, vehicle_number: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Vehicle Type</Label>
                  <Input
                    data-testid="edit-driver-vehicle-type"
                    value={editDriverDialog.driver.vehicle_type || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, vehicle_type: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Aadhaar Number</Label>
                  <Input
                    data-testid="edit-driver-aadhaar"
                    value={editDriverDialog.driver.aadhaar_number || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, aadhaar_number: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>License Number</Label>
                  <Input
                    data-testid="edit-driver-license"
                    value={editDriverDialog.driver.license_number || ""}
                    onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, license_number: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Account Status</Label>
                  <Select 
                    value={editDriverDialog.driver.account_status || "active"} 
                    onValueChange={(v) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, account_status: v } })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Driver Status</Label>
                  <Select 
                    value={editDriverDialog.driver.status || "offline"} 
                    onValueChange={(v) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, status: v } })}
                  >
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Verification Status */}
              <div className="pt-3 border-t">
                <p className="font-medium text-sm text-slate-700 mb-2">Document Verification Status</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Aadhaar Verification</Label>
                    <Select 
                      value={editDriverDialog.driver.verification_status?.aadhaar || "pending"} 
                      onValueChange={(v) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, verification_status: { ...editDriverDialog.driver.verification_status, aadhaar: v } } })}
                    >
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">License Verification</Label>
                    <Select 
                      value={editDriverDialog.driver.verification_status?.license || "pending"} 
                      onValueChange={(v) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, verification_status: { ...editDriverDialog.driver.verification_status, license: v } } })}
                    >
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Total Earnings (₹)</Label>
                <Input
                  type="number"
                  value={editDriverDialog.driver.total_earnings || 0}
                  onChange={(e) => setEditDriverDialog({ ...editDriverDialog, driver: { ...editDriverDialog.driver, total_earnings: parseFloat(e.target.value) || 0 } })}
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDriverDialog({ open: false, driver: null })} className="rounded-xl">Cancel</Button>
            <Button data-testid="save-driver-btn" onClick={handleUpdateDriver} disabled={loading} className="bg-slate-900 text-white rounded-xl">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Documents Dialog */}
      <Dialog open={reviewDocsDialog.open} onOpenChange={(o) => setReviewDocsDialog({ ...reviewDocsDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Review Driver Documents</DialogTitle>
            <DialogDescription>
              Driver: {reviewDocsDialog.doc?.driver_name} ({reviewDocsDialog.doc?.driver_phone})
            </DialogDescription>
          </DialogHeader>
          {reviewDocsDialog.doc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Profile Photo", url: reviewDocsDialog.doc.profile_photo },
                  { label: "Aadhaar Card", url: reviewDocsDialog.doc.aadhaar_url },
                  { label: "Driving License", url: reviewDocsDialog.doc.driving_license_url },
                  { label: "Vehicle Registration", url: reviewDocsDialog.doc.vehicle_registration_url },
                  { label: "Insurance", url: reviewDocsDialog.doc.insurance_url },
                ].map((doc, i) => (
                  <div key={i} className="border rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-2">{doc.label}</p>
                    {doc.url ? (
                      <a href={doc.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${doc.url}` : doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                        <Eye className="w-4 h-4" /> View
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">Not uploaded</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <Badge className={`rounded-full ${
                  reviewDocsDialog.doc.approval_status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  reviewDocsDialog.doc.approval_status === "rejected" ? "bg-red-100 text-red-700" :
                  "bg-amber-100 text-amber-700"
                }`}>
                  Current: {reviewDocsDialog.doc.approval_status}
                </Badge>
                <div className="flex gap-2">
                  <Button
                    data-testid="reject-docs-btn"
                    variant="outline"
                    className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleReviewDocuments(reviewDocsDialog.doc.user_id, "rejected")}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button
                    data-testid="approve-docs-btn"
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => handleReviewDocuments(reviewDocsDialog.doc.user_id, "approved")}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Ride Dialog */}
      <Dialog open={editRideDialog.open} onOpenChange={(o) => setEditRideDialog({ ...editRideDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Ride</DialogTitle>
            <DialogDescription>Update ride details</DialogDescription>
          </DialogHeader>
          {editRideDialog.ride && (
            <div className="space-y-4">
              <div>
                <Label>Fare Amount (₹)</Label>
                <Input
                  data-testid="edit-ride-fare"
                  type="number"
                  value={editRideDialog.ride.fare_amount || ""}
                  onChange={(e) => setEditRideDialog({ ...editRideDialog, ride: { ...editRideDialog.ride, fare_amount: parseFloat(e.target.value) } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Pickup Location</Label>
                <Input
                  data-testid="edit-ride-pickup"
                  value={editRideDialog.ride.pickup_location || ""}
                  onChange={(e) => setEditRideDialog({ ...editRideDialog, ride: { ...editRideDialog.ride, pickup_location: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Drop Location</Label>
                <Input
                  data-testid="edit-ride-drop"
                  value={editRideDialog.ride.drop_location || ""}
                  onChange={(e) => setEditRideDialog({ ...editRideDialog, ride: { ...editRideDialog.ride, drop_location: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={editRideDialog.ride.ride_status}
                  onValueChange={(v) => setEditRideDialog({ ...editRideDialog, ride: { ...editRideDialog.ride, ride_status: v } })}
                >
                  <SelectTrigger data-testid="edit-ride-status" className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="requested">Requested</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="started">Started</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select
                  value={editRideDialog.ride.payment_status}
                  onValueChange={(v) => setEditRideDialog({ ...editRideDialog, ride: { ...editRideDialog.ride, payment_status: v } })}
                >
                  <SelectTrigger data-testid="edit-ride-payment" className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-ride-btn" onClick={handleUpdateRide} disabled={loading} className="bg-slate-900 text-white rounded-xl">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Driver Dialog with Smart Matching */}
      <Dialog open={reassignDialog.open} onOpenChange={(o) => { setReassignDialog({ ...reassignDialog, open: o }); setMatchedDrivers([]); }}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign / Reassign Driver</DialogTitle>
            <DialogDescription>Smart matching based on distance, rating & response time</DialogDescription>
          </DialogHeader>
          
          {/* Auto-Assign Button */}
          <div className="flex gap-2 mb-4">
            <Button 
              data-testid="auto-assign-btn"
              onClick={handleAutoAssign} 
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl"
              disabled={loadingMatch || matchedDrivers.length === 0}
            >
              <Zap className="w-4 h-4 mr-2" /> Auto-Assign Best Driver
            </Button>
          </div>

          {/* Matched Drivers List */}
          <div className="flex-1 overflow-y-auto space-y-2 max-h-[300px]">
            {loadingMatch ? (
              <div className="text-center py-8 text-slate-500">Finding best matches...</div>
            ) : matchedDrivers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No available drivers found</div>
            ) : (
              matchedDrivers.map((d, idx) => (
                <div 
                  key={d.driver_id}
                  data-testid={`matched-driver-${d.driver_id}`}
                  onClick={() => setSelectedDriver(d.driver_id)}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedDriver === d.driver_id 
                      ? "border-forest bg-forest-50" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? "bg-amber-100 text-amber-700" : 
                        idx === 1 ? "bg-slate-200 text-slate-700" : 
                        idx === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        #{idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{d.name}</p>
                        <p className="text-xs text-slate-500">{d.vehicle_type} - {d.vehicle_number}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-forest">{d.total_score}</p>
                      <p className="text-xs text-slate-500">Match Score</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-blue-50 p-2 rounded-lg text-center">
                      <p className="text-blue-700 font-medium">{d.distance_km ? `${d.distance_km}km` : "N/A"}</p>
                      <p className="text-blue-500">Distance</p>
                    </div>
                    <div className="bg-amber-50 p-2 rounded-lg text-center">
                      <p className="text-amber-700 font-medium">{d.average_rating?.toFixed(1) || "N/A"} ★</p>
                      <p className="text-amber-500">Rating</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded-lg text-center">
                      <p className="text-green-700 font-medium">{d.completed_rides || 0}</p>
                      <p className="text-green-500">Rides</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Manual Selection Fallback */}
          <div className="pt-4 border-t mt-4">
            <p className="text-xs text-slate-500 mb-2">Or select manually:</p>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger data-testid="reassign-driver-select" className="rounded-xl">
                <SelectValue placeholder="Select driver" />
              </SelectTrigger>
              <SelectContent>
                {drivers.filter(d => d.status === "online").map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} - {d.vehicle_type} (Online)</SelectItem>
                ))}
                {drivers.filter(d => d.status !== "online").map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} - {d.vehicle_type} (Offline)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setReassignDialog({ open: false, rideId: "" })} className="rounded-xl">Cancel</Button>
            <Button data-testid="confirm-reassign-btn" onClick={handleReassignDriver} disabled={!selectedDriver} className="bg-slate-900 text-white rounded-xl">
              Assign Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Destination Dialog */}
      <Dialog open={addDestinationDialog} onOpenChange={setAddDestinationDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Destination</DialogTitle>
            <DialogDescription>Add a new pickup/drop location</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDestination} className="space-y-4">
            <div>
              <Label>Destination Name</Label>
              <Input
                data-testid="new-dest-name"
                value={newDestination.name}
                onChange={(e) => setNewDestination({ ...newDestination, name: e.target.value })}
                required
                placeholder="e.g., Poonch City"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>Region</Label>
              <Select value={newDestination.region} onValueChange={(v) => setNewDestination({ ...newDestination, region: v })}>
                <SelectTrigger data-testid="new-dest-region" className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Jammu">Jammu</SelectItem>
                  <SelectItem value="Kashmir">Kashmir</SelectItem>
                  <SelectItem value="Ladakh">Ladakh</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="submit-dest-btn" type="submit" disabled={loading} className="w-full bg-amber hover:bg-amber-dark text-white rounded-xl">
              Add Destination
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Destination Dialog */}
      <Dialog open={editDestinationDialog.open} onOpenChange={(o) => setEditDestinationDialog({ ...editDestinationDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Destination</DialogTitle>
          </DialogHeader>
          {editDestinationDialog.dest && (
            <div className="space-y-4">
              <div>
                <Label>Destination Name</Label>
                <Input
                  data-testid="edit-dest-name"
                  value={editDestinationDialog.dest.name || ""}
                  onChange={(e) => setEditDestinationDialog({ ...editDestinationDialog, dest: { ...editDestinationDialog.dest, name: e.target.value } })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Region</Label>
                <Select
                  value={editDestinationDialog.dest.region}
                  onValueChange={(v) => setEditDestinationDialog({ ...editDestinationDialog, dest: { ...editDestinationDialog.dest, region: v } })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Jammu">Jammu</SelectItem>
                    <SelectItem value="Kashmir">Kashmir</SelectItem>
                    <SelectItem value="Ladakh">Ladakh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dest-active"
                  checked={editDestinationDialog.dest.is_active}
                  onChange={(e) => setEditDestinationDialog({ ...editDestinationDialog, dest: { ...editDestinationDialog.dest, is_active: e.target.checked } })}
                  className="rounded"
                />
                <Label htmlFor="dest-active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-dest-btn" onClick={handleUpdateDestination} disabled={loading} className="bg-slate-900 text-white rounded-xl">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fare Dialog */}
      <Dialog open={addFareDialog} onOpenChange={setAddFareDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Fare Entry</DialogTitle>
            <DialogDescription>Set fare between two destinations</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddFare} className="space-y-4">
            <div>
              <Label>From Destination</Label>
              <Select value={newFare.from_destination_id} onValueChange={(v) => setNewFare({ ...newFare, from_destination_id: v })}>
                <SelectTrigger data-testid="new-fare-from" className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select pickup" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.filter(d => d.is_active).map((d) => (
                    <SelectItem key={d.destination_id} value={d.destination_id}>{d.name} ({d.region})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Destination</Label>
              <Select value={newFare.to_destination_id} onValueChange={(v) => setNewFare({ ...newFare, to_destination_id: v })}>
                <SelectTrigger data-testid="new-fare-to" className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select drop" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.filter(d => d.is_active).map((d) => (
                    <SelectItem key={d.destination_id} value={d.destination_id}>{d.name} ({d.region})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service Type</Label>
              <Select value={newFare.service_type} onValueChange={(v) => setNewFare({ ...newFare, service_type: v })}>
                <SelectTrigger data-testid="new-fare-service" className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((st) => (
                    <SelectItem key={st.slug} value={st.slug}>{st.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Fare (₹)</Label>
                <Input
                  data-testid="new-fare-base"
                  type="number"
                  value={newFare.base_fare}
                  onChange={(e) => setNewFare({ ...newFare, base_fare: e.target.value })}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Final Fare (₹)</Label>
                <Input
                  data-testid="new-fare-amount"
                  type="number"
                  value={newFare.fare_amount}
                  onChange={(e) => setNewFare({ ...newFare, fare_amount: e.target.value })}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>
            <Button data-testid="submit-fare-btn" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl">
              Add Fare Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Fare Dialog */}
      <Dialog open={editFareDialog.open} onOpenChange={(o) => setEditFareDialog({ ...editFareDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Fare</DialogTitle>
          </DialogHeader>
          {editFareDialog.fare && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-sm text-slate-500">Route</p>
                <p className="font-medium text-slate-900">
                  {destinations.find(d => d.destination_id === editFareDialog.fare.from_destination_id)?.name || "N/A"} → {destinations.find(d => d.destination_id === editFareDialog.fare.to_destination_id)?.name || "N/A"}
                </p>
                <p className="text-xs text-slate-400 capitalize">{editFareDialog.fare.service_type.replace(/_/g, " ")}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base Fare (₹)</Label>
                  <Input
                    data-testid="edit-fare-base"
                    type="number"
                    value={editFareDialog.fare.base_fare || ""}
                    onChange={(e) => setEditFareDialog({ ...editFareDialog, fare: { ...editFareDialog.fare, base_fare: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Final Fare (₹)</Label>
                  <Input
                    data-testid="edit-fare-amount"
                    type="number"
                    value={editFareDialog.fare.fare_amount || ""}
                    onChange={(e) => setEditFareDialog({ ...editFareDialog, fare: { ...editFareDialog.fare, fare_amount: e.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-fare-btn" onClick={handleUpdateFare} disabled={loading} className="bg-slate-900 text-white rounded-xl">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
